import store from '../../redux/store';
import * as THREE from 'three';
import {
  updateAssetVisibility,
  lightDataUpdate,
  cameraPropertiesUpdate,
} from '../../redux/Slice/gridSlice';
import { updateParticle } from '../../redux/Slice/particleSlice';
import { applyLightVisibility } from './lightHelperUtils';
import { resolveSceneCameraObject } from './sceneCameraUtils';
import { pauseAnimation } from '../particles/animationControls';
import {
  glbHierarchyNodeMatchesUuid,
  isGlbHierarchyNodeItem,
} from '../../components/gridpagefunction/MeshExtractation';

const REDUX_ASSET_KEYS = new Set([
  'image',
  'mesh',
  'video',
  'audio',
  'text',
  'hotspot',
  'empty',
]);

const PARTICLE_OBJ_TYPES = new Set([
  'rain',
  'snow',
  'fire',
  'stars',
  'water',
  'foliage',
  'clouds',
  'terrain',
]);

export function isParticleSceneObject(sceneObj) {
  if (!sceneObj) return false;
  return (
    PARTICLE_OBJ_TYPES.has(sceneObj.objType) ||
    sceneObj.deleteType === 'particle' ||
    !!sceneObj.userData?.isParticle
  );
}

export function hasValidParticleCount(value) {
  if (value === '' || value === null || value === undefined) return false;
  const n = Number(value);
  return Number.isFinite(n) && n >= 1;
}

export function hasValidParticleSize(value) {
  if (value === '' || value === null || value === undefined) return false;
  const n = Number(value);
  return Number.isFinite(n) && n > 0;
}

const PARTICLE_TYPES_REQUIRING_COUNT = new Set([
  'rain',
  'snow',
  'clouds',
  'stars',
  'terrain',
]);

const PARTICLE_TYPES_REQUIRING_SIZE = new Set([
  'rain',
  'snow',
  'clouds',
  'stars',
  'fire',
  'foliage',
]);

/** Returns false when required Count/Size fields are cleared or invalid. */
export function isParticleConfigRenderable(particle, typeOverride) {
  if (!particle) return true;

  const type = (typeOverride || particle.type || particle.objType || '')
    .toString()
    .toLowerCase();
  if (!PARTICLE_OBJ_TYPES.has(type)) return true;

  let countOk = true;
  let sizeOk = true;

  if (PARTICLE_TYPES_REQUIRING_COUNT.has(type)) {
    countOk = hasValidParticleCount(particle.particleCount);
  }
  if (PARTICLE_TYPES_REQUIRING_SIZE.has(type)) {
    sizeOk = hasValidParticleSize(particle.size);
  }

  return countOk && sizeOk;
}

export function applyParticleVisibility(sceneObj, visible) {
  if (!sceneObj) return;

  sceneObj.userData = sceneObj.userData || {};
  sceneObj.userData.editorVisible = visible;

  if (!visible) {
    sceneObj.visible = false;
    sceneObj.traverse((child) => {
      child.visible = false;
      if (child.userData) {
        child.userData.editorVisible = false;
      }
    });

    const particleId = sceneObj.name || sceneObj.url;
    if (particleId) {
      pauseAnimation(sceneObj, particleId);
    }
    return;
  }

  sceneObj.visible = true;

  if (sceneObj.userData.lifeExpired || sceneObj.userData.runtimeHidden) {
    applyParticleRuntimeHidden(sceneObj, true);
    return;
  }

  sceneObj.traverse((child) => {
    child.visible = true;
    if (child.userData) {
      child.userData.editorVisible = true;
    }
  });
}

function getParticleMaterials(sceneObj) {
  if (!sceneObj?.material) return [];
  return Array.isArray(sceneObj.material)
    ? sceneObj.material
    : [sceneObj.material];
}

/** Fire and other leaf particle systems are a single Points/Mesh — hide via material, not object.visible. */
function applyLeafParticleRuntimeHidden(sceneObj, hidden) {
  const materials = getParticleMaterials(sceneObj);
  if (!materials.length) return;

  if (hidden) {
    if (sceneObj.userData._lifeSavedMaterialVisible === undefined) {
      sceneObj.userData._lifeSavedMaterialVisible =
        materials[0].visible !== false;
      sceneObj.userData._lifeSavedOpacity = materials[0].opacity;
    }
    materials.forEach((mat) => {
      if (!mat) return;
      mat.visible = false;
      mat.opacity = 0;
      mat.transparent = true;
      mat.needsUpdate = true;
    });
    return;
  }

  materials.forEach((mat) => {
    if (!mat) return;
    if (sceneObj.userData._lifeSavedMaterialVisible !== undefined) {
      mat.visible = sceneObj.userData._lifeSavedMaterialVisible;
      delete sceneObj.userData._lifeSavedMaterialVisible;
    } else {
      mat.visible = true;
    }
    if (sceneObj.userData._lifeSavedOpacity !== undefined) {
      mat.opacity = sceneObj.userData._lifeSavedOpacity;
      delete sceneObj.userData._lifeSavedOpacity;
    }
    mat.needsUpdate = true;
  });
}

function isLeafParticleVisual(sceneObj) {
  return (
    sceneObj?.deleteType === 'particle' &&
    (sceneObj.isPoints || sceneObj.isMesh)
  );
}

/** Hide particle visuals after life ends while keeping the root selectable in the hierarchy. */
export function applyParticleRuntimeHidden(sceneObj, hidden) {
  if (!sceneObj) return;

  sceneObj.userData = sceneObj.userData || {};
  sceneObj.userData.runtimeHidden = hidden;
  sceneObj.visible = true;

  if (isLeafParticleVisual(sceneObj)) {
    applyLeafParticleRuntimeHidden(sceneObj, hidden);
    return;
  }

  sceneObj.traverse((child) => {
    if (child === sceneObj) return;
    if (child.userData?.isParticlePickProxy) {
      child.visible = !hidden;
      return;
    }

    if (!child.isMesh && !child.isPoints && !child.isGroup) return;

    if (hidden) {
      if (child.userData._lifeSavedVisible === undefined) {
        child.userData._lifeSavedVisible = child.visible;
      }
      child.visible = false;
    } else if (child.userData._lifeSavedVisible !== undefined) {
      child.visible = child.userData._lifeSavedVisible;
      delete child.userData._lifeSavedVisible;
    } else {
      child.visible = true;
    }
  });
}

/** Pick life-ended / runtime-hidden particle roots when mesh raycasts miss. */
export function pickHiddenParticleFromRay(raycaster, scene, particles = []) {
  if (!raycaster || !scene) return null;

  let closest = null;
  let closestDist = Infinity;
  const center = new THREE.Vector3();

  scene.traverse((obj) => {
    if (obj.deleteType !== 'particle') return;
    if (
      !obj.userData?.lifeExpired &&
      !obj.userData?.runtimeHidden &&
      obj.visible !== false
    ) {
      return;
    }
    if (obj.userData?.editorVisible === false) return;

    const config = particles.find(
      (p) => p.id === obj.name || p.id === obj.url || p.name === obj.name
    );
    const range = Number(config?.range ?? obj.userData?.range ?? 5);
    const radius = Math.max(1, range * 2);

    obj.getWorldPosition(center);
    const distToRay = raycaster.ray.distanceToPoint(center);
    if (distToRay > radius) return;

    const dist = raycaster.ray.origin.distanceTo(center);
    if (dist < closestDist) {
      closestDist = dist;
      closest = obj;
    }
  });

  return closest;
}

function resolveParticleSceneObject(scene, item) {
  if (!scene || !item) return null;

  const ids = [item.id, item._id, item.name, item.url].filter(Boolean);

  for (const id of ids) {
    const obj = scene.getObjectByName(id);
    if (obj && isParticleSceneObject(obj)) return obj;
  }

  let found = null;
  scene.traverse((child) => {
    if (found || !isParticleSceneObject(child)) return;
    if (ids.some((id) => child.name === id || child.url === id)) {
      found = child;
    }
  });

  return found;
}

export function resolveAssetObjectId(item, type) {
  if (!item) return null;
  if (type === 'camera' || type === 'light' || type === 'particle') {
    return item._id || item.id;
  }
  return (
    item._id ||
    item.id ||
    item.url ||
    item.name ||
    item.temp_file_name ||
    item.text_material?.id ||
    item.userData?.text_material?.id ||
    item.userData?.id
  );
}

export function isLiveSceneObject3D(obj) {
  return !!(
    obj &&
    typeof obj.updateMatrixWorld === 'function' &&
    typeof obj.traverse === 'function'
  );
}

function collectAssetLookupIds(item) {
  if (!item) return [];
  return [
    item._id,
    item.id,
    item.url,
    item.name,
    item.temp_file_name,
    item.material_settings?.id,
    item.userData?.material_settings?.id,
    item.text_material?.id,
    item.userData?.text_material?.id,
  ].filter(Boolean);
}

function resolveTextSceneObject(scene, item) {
  if (!scene || !item) return null;

  const ids = collectAssetLookupIds(item);
  for (const id of ids) {
    const obj = scene.getObjectByName(id);
    if (obj?.deleteType === 'text') return obj;
  }

  let found = null;
  scene.traverse((child) => {
    if (found || child.deleteType !== 'text') return;
    const childIds = [
      child.name,
      child._id,
      child.url,
      child.temp_file_name,
      child.userData?.text_material?.id,
    ].filter(Boolean);
    if (ids.some((id) => childIds.includes(id))) {
      found = child;
    }
  });
  return found;
}

function resolvePlySceneObject(scene, item) {
  if (!scene || !item) return null;

  const ids = collectAssetLookupIds(item);
  for (const id of ids) {
    const obj = scene.getObjectByName(id);
    if (obj?.glb_type === 'ply') return obj;
  }

  let found = null;
  scene.traverse((child) => {
    if (found || child.glb_type !== 'ply') return;
    if (
      ids.some(
        (id) =>
          child.name === id ||
          child.url === id ||
          child._id === id ||
          child.userData?.material_settings?.id === id
      )
    ) {
      found = child;
    }
  });
  return found;
}

/** Always return the in-scene Three.js object, not a plain Redux asset record. */
export function resolveLiveSceneObject(scene, item, type) {
  if (!scene || !item) return null;

  if (item?.glb_type === 'ply' || item?.userData?.glb_type === 'ply') {
    const plyObj = resolvePlySceneObject(scene, item);
    if (plyObj) return plyObj;
  }

  const fromScene = resolveSceneObject(scene, item, type);
  if (fromScene) return fromScene;

  if (isLiveSceneObject3D(item)) {
    let inScene = false;
    scene.traverse((child) => {
      if (child === item) inScene = true;
    });
    if (inScene) return item;
  }

  const id = resolveAssetRowId(item);
  if (!id) return null;

  return (
    scene.getObjectByName(id) ||
    resolvePlySceneObject(scene, { _id: id, id, name: id, url: id })
  );
}

export function resolveAssetRowId(item) {
  if (!item) return null;
  if (isLiveSceneObject3D(item)) {
    return item._id || item.url || item.name;
  }
  return (
    item._id ||
    item.id ||
    item.temp_file_name ||
    item.url ||
    item.name ||
    item.material_settings?.id ||
    item.userData?.material_settings?.id
  );
}

export function isAssetRowSelected(selectObject, item, type, scene) {
  if (!selectObject || typeof selectObject !== 'object') return false;

  const hasSelectionIdentity =
    isLiveSceneObject3D(selectObject) ||
    selectObject.uuid ||
    selectObject._id ||
    selectObject.id ||
    selectObject.url ||
    selectObject.name;
  if (!hasSelectionIdentity) return false;

  // GLB inner hierarchy nodes share names across duplicates — uuid match only.
  if (isGlbHierarchyNodeItem(item)) {
    const liveSelect = isLiveSceneObject3D(selectObject)
      ? selectObject
      : resolveLiveSceneObject(
          scene,
          selectObject,
          type || selectObject?.deleteType || 'mesh'
        ) || selectObject;
    return glbHierarchyNodeMatchesUuid(item, liveSelect?.uuid);
  }

  const sceneObj = resolveLiveSceneObject(
    scene,
    item,
    type || item?.deleteType
  );
  const liveSelect = isLiveSceneObject3D(selectObject)
    ? selectObject
    : resolveLiveSceneObject(
        scene,
        selectObject,
        type || selectObject?.deleteType || 'mesh'
      ) || selectObject;

  if (liveSelect?.uuid && sceneObj?.uuid === liveSelect.uuid) return true;
  if (sceneObj && liveSelect === sceneObj) return true;
  if (liveSelect?.uuid && item?.uuid === liveSelect.uuid) return true;

  const rowIds = new Set(
    [
      resolveAssetRowId(item),
      ...collectAssetLookupIds(item),
      sceneObj?._id,
      sceneObj?.name,
      sceneObj?.url,
      sceneObj?.uuid,
      item?.uuid,
    ].filter(Boolean)
  );

  const selectIds = [
    liveSelect?._id,
    liveSelect?.id,
    liveSelect?.url,
    liveSelect?.name,
    liveSelect?.uuid,
    selectObject?._id,
    selectObject?.id,
    selectObject?.url,
    selectObject?.name,
    selectObject?.uuid,
  ].filter(Boolean);

  return selectIds.some((id) => rowIds.has(id));
}

export function resolveSceneObject(scene, item, type) {
  if (!scene || !item) return null;

  const ids = [
    item._id,
    item.id,
    item.url,
    item.name,
    item.temp_file_name,
    item.material_settings?.id,
    item.userData?.material_settings?.id,
    item.text_material?.id,
    item.userData?.text_material?.id,
    item.object?.name,
  ].filter(Boolean);

  for (const id of ids) {
    const obj = scene.getObjectByName(id);
    if (obj) return obj;
  }

  if (type === 'light' && item.id) {
    const byLight = scene.children.find(
      (child) =>
        child.isLight &&
        (child.name === item.id || child.userData?.lightId === item.id)
    );
    if (byLight) return byLight;
  }

  if (type === 'camera') {
    return resolveSceneCameraObject(scene, item);
  }

  if (type === 'particle') {
    return resolveParticleSceneObject(scene, item);
  }

  if (type === 'text' || item?.deleteType === 'text') {
    const textObj = resolveTextSceneObject(scene, item);
    if (textObj) return textObj;
  }

  if (
    type === 'mesh' ||
    item?.glb_type === 'ply' ||
    item?.deleteType === 'mesh'
  ) {
    const plyObj = resolvePlySceneObject(scene, item);
    if (plyObj) return plyObj;
  }

  return null;
}

export function getParticleEditorVisible(item, sceneObj) {
  if (item?.visible === false) return false;
  if (sceneObj?.userData?.editorVisible === false) return false;
  return true;
}

function isParticleVisibilityItem(item, sceneObj) {
  return (
    item?.type === 'particle' ||
    item?.deleteType === 'particle' ||
    isParticleSceneObject(sceneObj)
  );
}

export function isAssetVisible(item, sceneObj) {
  if (isParticleVisibilityItem(item, sceneObj)) {
    return getParticleEditorVisible(item, sceneObj);
  }
  if (item?.visible === false) return false;
  if (sceneObj && sceneObj.visible === false) return false;
  return true;
}

export function getPublishAssetVisible(sceneObj, reduxItem) {
  if (isParticleVisibilityItem(reduxItem, sceneObj)) {
    return getParticleEditorVisible(reduxItem, sceneObj);
  }
  if (reduxItem?.visible === false) return false;
  if (sceneObj && sceneObj.visible === false) return false;
  return true;
}

const ASSET_TYPE_TO_REDUX_KEY = {
  mesh: 'mesh',
  image: 'image',
  video: 'video',
  audio: 'audio',
  text: 'text',
  hotspot: 'hotspot',
};

/** Match a scene/redux item to its assets_data record for publish. */
export function findReduxAssetRecord(item, assetsData, arrKeyOverride) {
  if (!item || !assetsData) return null;

  const arrKey =
    arrKeyOverride ||
    ASSET_TYPE_TO_REDUX_KEY[item.deleteType] ||
    ASSET_TYPE_TO_REDUX_KEY[item.type];
  const list = arrKey ? assetsData[arrKey] : null;
  if (!list?.length) return null;

  const ids = [
    item._id,
    item.id,
    item.name,
    item.url,
    item.temp_file_name,
    item.text_material?.id,
    item.userData?.text_material?.id,
  ].filter(Boolean);

  return list.find((a) => {
    const assetIds = [
      a._id,
      a.id,
      a.name,
      a.url,
      a.temp_file_name,
      a.text_material?.id,
    ].filter(Boolean);
    return ids.some((id) => assetIds.includes(id));
  });
}

/** Resolve scene object + redux record and return publish `visible` flag. */
export function getPublishVisibleForAsset(
  scene,
  item,
  assetsData,
  { type, arrKey } = {}
) {
  if (!item) return true;

  const assetType = type || item.deleteType || item.type;
  const sceneObj = item?.isObject3D
    ? item
    : resolveSceneObject(scene, item, assetType);
  const reduxItem =
    findReduxAssetRecord(item, assetsData, arrKey) ||
    (assetType === 'particle' || assetType === 'light' || assetType === 'camera'
      ? item
      : null);

  return getPublishAssetVisible(sceneObj, reduxItem || item);
}

/** Include editor `visible` on each particle for publish / save payloads. */
export function mapParticlesForPublish(particles = []) {
  if (!Array.isArray(particles) || particles.length === 0) return [];

  return particles.map((particle) => ({
    ...particle,
    // Editor visibility only — not scene.visible (life cycle can hide the mesh temporarily).
    visible: particle.visible !== false,
  }));
}

export function applyVisibilityToSceneObject(sceneObj, visible, scene = null) {
  if (!sceneObj) return;

  if (isParticleSceneObject(sceneObj)) {
    applyParticleVisibility(sceneObj, visible);
    return;
  }

  if (sceneObj.isLight) {
    const lightData = store.getState().assets_object.light_data || [];
    const record =
      lightData.find(
        (l) => l.id === sceneObj.name || l.name === sceneObj.name
      ) || { id: sceneObj.name, visible };
    const windowType = store.getState().window?.window_type;
    applyLightVisibility(
      sceneObj,
      { ...record, visible },
      scene,
      { isDesignMode: windowType === 'design' }
    );
  } else {
    sceneObj.visible = visible;
  }

  if (sceneObj.isCamera) {
    scene?.traverse?.((child) => {
      if (child.type === 'CameraHelper') {
        child.visible = visible;
      }
    });
  }
}

function syncReduxVisibility({ item, type, arrKey, objectId, visible }) {
  if (REDUX_ASSET_KEYS.has(arrKey)) {
    store.dispatch(
      updateAssetVisibility({
        arrKey,
        objectId,
        visible,
      })
    );
    return;
  }

  if (type === 'light' && item?.id) {
    store.dispatch(
      lightDataUpdate({
        actionType: 'update',
        light: { ...item, id: item.id, visible },
      })
    );
    return;
  }

  if (type === 'camera') {
    const camId = item._id || item.id;
    if (camId) {
      store.dispatch(
        cameraPropertiesUpdate({
          ...item,
          _id: camId,
          id: camId,
          visible,
        })
      );
    }
    return;
  }

  if (type === 'particle' && (item?.id || item?._id)) {
    store.dispatch(
      updateParticle({
        id: item.id || item._id,
        updates: { visible },
      })
    );
  }
}

export function toggleAssetVisibility({
  scene,
  item,
  type,
  arrKey,
  objectId: objectIdOverride,
}) {
  const objectId = objectIdOverride || resolveAssetObjectId(item, type);
  const sceneObj = resolveSceneObject(scene, item, type);
  const nextVisible = !isAssetVisible(item, sceneObj);

  if (sceneObj) {
    if (type === 'particle' || isParticleSceneObject(sceneObj)) {
      applyParticleVisibility(sceneObj, nextVisible);
    } else {
      applyVisibilityToSceneObject(sceneObj, nextVisible, scene);
    }
  }

  syncReduxVisibility({ item, type, arrKey, objectId, visible: nextVisible });

  return nextVisible;
}

export function applyLoadedAssetsVisibility(scene, contentData) {
  if (!scene || !contentData) return;

  const applyList = (list, type) => {
    (list || []).forEach((asset) => {
      if (asset.visible === false) {
        const sceneObj = resolveSceneObject(scene, asset, type);
        if (type === 'particle' || isParticleSceneObject(sceneObj)) {
          applyParticleVisibility(sceneObj, false);
        } else {
          applyVisibilityToSceneObject(sceneObj, false, scene);
        }
      }
    });
  };

  applyList(contentData.mesh, 'mesh');
  applyList(contentData.image, 'image');
  applyList(contentData.video, 'video');
  applyList(contentData.audio, 'audio');
  applyList(contentData.text, 'text');
  applyList(contentData.hotspots, 'hotspot');
  applyList(contentData.particle, 'particle');
  applyList(contentData.camera_properties || contentData.cameras, 'camera');
  applyList(
    contentData.light_data || contentData.light || contentData.lights,
    'light'
  );
}

/** Apply visibility flags from Redux to the live scene (design + preview). */
export function syncSceneVisibilityFromStore(scene) {
  if (!scene) return;

  const state = store.getState();
  const assetsData = state.assets_object?.assets_data || {};
  const particles = state.particles?.particles || [];
  const lightData = state.assets_object?.light_data || [];
  const cameras = state.assets_object?.camera_properties || [];

  const applyRecord = (item, type, arrKey) => {
    if (!item) return;
    const visible = item.visible !== false;
    const sceneObj = resolveSceneObject(scene, item, type);
    if (!sceneObj) return;

    if (type === 'particle' || isParticleSceneObject(sceneObj)) {
      const configRenderable = isParticleConfigRenderable(
        item,
        sceneObj.objType || item.type
      );
      const lifeExpired = sceneObj.userData?.lifeExpired === true;
      if (!visible) {
        applyParticleVisibility(sceneObj, false);
      } else if (!lifeExpired) {
        applyParticleVisibility(sceneObj, configRenderable);
      }
      return;
    }

    applyVisibilityToSceneObject(sceneObj, visible, scene);
  };

  for (const arrKey of REDUX_ASSET_KEYS) {
    (assetsData[arrKey] || []).forEach((item) => {
      const type =
        arrKey === 'empty' ? 'mesh' : arrKey === 'hotspot' ? 'hotspot' : arrKey;
      applyRecord(item, type, arrKey);
    });
  }

  particles.forEach((item) => applyRecord(item, 'particle', 'particle'));
  lightData.forEach((item) =>
    applyRecord({ ...item, id: item.id || item._id }, 'light', 'light')
  );
  cameras.forEach((item) => applyRecord(item, 'camera', 'camera'));
}
